import { HttpHeaders } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '@services/alert.service';
import { ApiService } from '@services/api.service';
import { FormatCurrencyService } from '@services/format-currency.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  interval,
  Subscription,
} from 'rxjs';
import { MatDialogCustomComponent } from '../mat-dialog-custom/mat-dialog-custom.component';
import { Router } from '@angular/router';

const INPUT_DEBOUNCE_TIME = 500; // 0.5s
const POLLING_INTERVAL = 60_000; // 60s
const DELAY_DURATION = 1000;

@Component({
  selector: 'ew-invoice-funds',
  templateUrl: './invoice-funds.component.html',
  styleUrl: './invoice-funds.component.scss',
})
export class InvoiceFundsComponent {
  showInvoiceFundsFeatureFlag: boolean = false;
  currencies: any[] = [];
  loadingCurrencies: boolean = false;
  loadingRate: boolean = false;
  loadingCreateInvoiceFunds: boolean = false;
  lockSendSubmit: boolean = false;
  amount: any = new FormControl(0, [Validators.required]);
  currency: any = new FormControl('', [Validators.required]);
  rate: any = { amountFrom: 0, amountTo: 0 };
  bellowMinimalAmount: string = 'invoiceFunds:confirmationMinimalAmountMessage';
  notEnoughBalance: string = '';
  invoiceNumber = new FormControl('', [
    Validators.required,
    Validators.pattern('^[A-Za-z0-9-]{1,}$'),
  ]);
  providerName = new FormControl('', [Validators.required]);
  apiPolling!: Subscription;
  payoutUpdatedValues: {
    balanceBefore: string;
    balanceAfter: string;
    fxRateBaseCurrency: string;
    fxRateDestCurrency: string;
  } = {
    balanceBefore: '',
    balanceAfter: '',
    fxRateBaseCurrency: '',
    fxRateDestCurrency: '',
  };
  unlockSendButton: boolean = true;

  constructor(
    private API: ApiService,
    private formatCurrency: FormatCurrencyService,
    private DIALOG: MatDialogRef<MatDialogCustomComponent>,
    private alertService: AlertService,
    private router: Router,
  ) {
    this.getCurrencyAccounts();

    this.amount.valueChanges
      .pipe(
        debounceTime(INPUT_DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter((value) => this.validateAmountInput(value)),
        switchMap((value) => this.callGetRate(value)),
      )
      .subscribe({
        next: (data: any) => this.handleApiResponse(data),
      });

    this.apiPolling = interval(POLLING_INTERVAL)
      .pipe(
        filter(() => this.validateAmountPolling(this.amount.value)),
        switchMap(() => this.callGetRate(this.amount.value)),
      )
      .subscribe({
        next: (data: any) => this.handleApiResponse(data),
      });

    this.amount.setValue(1);
  }

  get validations() {
    const rateRequestFinished = this.rate.amountFrom;

    const validationsWithoutFirstStep = [
      this.currency.valid,
      rateRequestFinished &&
        this.invoiceNumber.valid &&
        this.providerName.valid &&
        !this.lockSendSubmit,
    ];

    return validationsWithoutFirstStep;
  }

  get getPayload() {
    return {
      currencyAccountId: this.currency.value.companyCurrencyAccountId,
      amount: this.rate.amountFrom,
    };
  }

  validateAmountInput(value: any): boolean {
    value = parseFloat(value);
    if (isNaN(value)) {
      return false;
    }

    if (value < 1) {
      this.alertService.warning(this.bellowMinimalAmount);
      this.lockSendSubmit = true;
      this.getUpdatedValues();
      return false;
    }

    this.lockSendSubmit = false;
    return true;
  }

  callGetRate(value: any) {
    if (typeof value === 'string') {
      value = parseFloat(value);
    }

    this.loadingRate = true;
    this.rate = {
      ...this.rate,
      amountFrom: value,
      amountTo: 0,
    };

    return new Promise((res) => {
      res({
        data: {
          ...this.rate,
        },
      });
    });
  }

  getCurrencySymbol(currency: string): string {
    return this.formatCurrency.getCurrencySymbol(currency);
  }

  handleApiResponse(data: any) {
    this.rate = data.data;
    const fxRate = this.rate.amountTo / this.rate.amountFrom;
    this.rate.fx = fxRate.toFixed(2);
    this.loadingRate = false;
    this.getUpdatedValues();
  }

  getUpdatedValues(): void {
    let balanceBefore = '';
    let balanceAfter = '';
    let fxRateBaseCurrency = '';
    let fxRateDestCurrency = '';

    if (this.currency) {
      balanceBefore = this.formatCurrency.formatAmount(
        this.currency.value.balance,
        this.currency.value.currencyIso,
      );

      balanceAfter =
        this.getCurrencySymbol(this.currency.value.currencyIso) + ' - ';

      fxRateBaseCurrency = this.formatCurrency.formatAmount(
        1,
        this.currency.value.currencyIso,
      );
    }

    if (this.amount.value >= 1) {
      balanceAfter = this.formatCurrency.formatAmount(
        this.currency.value.balance + this.amount.value,
        this.currency.value.currencyIso,
      );
    }

    if (this.rate.amountTo) {
      fxRateDestCurrency = this.formatCurrency.formatAmount(
        this.rate.fx,
        this.rate.dstCurrency,
      );
    }

    this.payoutUpdatedValues = {
      balanceBefore,
      balanceAfter,
      fxRateBaseCurrency,
      fxRateDestCurrency,
    };
  }

  getCurrencyAccounts(): void {
    this.loadingCurrencies = true;
    this.API.get('v1/company-currency-accounts/').subscribe((data: any) => {
      this.currencies = data.records;

      this.loadingCurrencies = false;
    });
  }

  createInvoiceFund(): void {
    this.stopApiPolling();

    this.lockSendSubmit = true;

    var headers = new HttpHeaders();
    this.loadingCreateInvoiceFunds = true;
    this.API.post(
      `/v1/invoices`,
      {
        companyCurrencyAccountId: this.currency.value.companyCurrencyAccountId,
        amountToSend: `${this.amount.value}`,
        invoiceNumber: this.invoiceNumber.value,
        providerName: this.providerName.value,
      },
      {
        headers: headers,
      },
    ).subscribe(
      (data: any) => {
        this.API.alertService.success('invoiceFunds:messageSuccess');
        setTimeout(() => {
          this.loadingCreateInvoiceFunds = false;
          this.DIALOG.close();
          this.router.navigate([`/invoice-funds/${data.records.id}/details`]);
        }, DELAY_DURATION);
      },
      (err) => {
        this.API.alertService.error('invoiceFunds:messageError');
        this.loadingCreateInvoiceFunds = false;
        this.lockSendSubmit = false;
      },
    );
  }

  private validateAmountPolling(value: any): boolean {
    value = parseFloat(value);

    if (isNaN(value) || value < 1 || value > this.currency.value.balance) {
      return false;
    }

    return true;
  }

  private stopApiPolling(): void {
    if (this.apiPolling) {
      this.apiPolling.unsubscribe();
    }
  }
}
